"use strict";
/* globals module */
/**
 * @module userClient
 * @description  User Client provides an easy way to consume  User REST API end-points. In order to obtain needed routes `userClient` uses `userRoute`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
;
var httpApi_1 = require("../../../httpApi");
var _1 = require("./");
var UserClient = /** @class */ (function () {
    function UserClient(userRoute, apiClient) {
        this.userRoute = userRoute;
        this.apiClient = apiClient;
    }
    Object.defineProperty(UserClient.prototype, "routeDefinition", {
        /**
         * Provides direct access to `userRoute`.
         * @method
         * @example userClient.routeDefinition.get();
         **/
        get: function () {
            return this.userRoute;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the exists action has been performed. This action checks if user exists in the application.
     * @method
     * @param username A username which uniquely identifies an application user.
     * @example userClient.exists('<username>')
                    .then(function (data) {
                        // perform success action here
                    },
                     function (response, status, headers, config) {
                        // perform error handling here
                    });
     **/
    UserClient.prototype.exists = function (username, options) {
        return this.apiClient.get(this.userRoute.exists(username, options));
    };
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of user resources matching the given criteria.
     * @method
     * @param options Query resource options object.
     * @example userClient.find({
                    pageNumber : 1,
                    pageSize : 10,
                    orderBy : '<field>',
                    orderDirection : '<asc|desc>',
                    search : '<search-phrase>'
                })
                .then(function (collection) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                    // perform error handling here
                });
     **/
    UserClient.prototype.find = function (options) {
        return this.apiClient.get(this.routeDefinition.find(options));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns the specified user resource.
     * @method
     * @param id A username or id which uniquely identifies an application user whose account information needs to be retrieved.
     * @param options Query resources options.
     * @returns A promise that is resolved once the get action has been performed.
     * @example userClient.get({
                    username : '<username>',
                    embed : '<embedded-resource>'
                })
                .then(function (data) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                    // perform error handling here
                });
     **/
    UserClient.prototype.get = function (id, options) {
        return this.apiClient.get(this.userRoute.get(id, options));
    };
    /**
     * Returns a promise that is resolved once the create user action has been performed; this action creates a new user.
     * @method
     * @param data An user object that needs to be inserted into the system.
     * @returns A promise that is resolved once the create user action has been performed.
     * @example userClient.create({
                    email : '<email>',
                    password : '<password>',
                    userName : '<username>',
                    additionalProperty: '<additional-property>'
                })
                .then(function (data) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                    // perform error handling here
                });
     **/
    UserClient.prototype.create = function (data) {
        return this.apiClient.post(this.routeDefinition.create(), this.routeDefinition.createParams(data));
    };
    /**
     * Returns a promise that is resolved once the update user action has been performed; this action updates a user. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `userRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(user);
     * let uri = params['model'].links('put').href;
     * ```
     * @param data A user object used to update specified user resource.
     * @returns A promise that is resolved once the update user action has been performed.
     * @method
     * @example // user is a resource previously fetched using get action.
                    user.roles = ['<role-name>', '<new-role-name>'];
                    user.email = '<new-email>';
                    userClient.update(user)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    UserClient.prototype.update = function (data) {
        return this.apiClient.put(this.routeDefinition.update(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the remove user action has been performed. This action will remove a user from the system if successfully completed. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `userRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(user);
     * let uri = params['model'].links('delete').href;
     * ```
     * @method
     * @param data  A user object used to delete specified user resource.
     * @returns A promise that is resolved once the remove user action has been performed.
     * @example // user is a resource previously fetched using get action.
                    userClient.remove(user)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    UserClient.prototype.remove = function (data) {
        return this.apiClient.delete(this.routeDefinition.delete(data));
    };
    /**
     * Returns a promise that is resolved once the unlock user action has been performed. This action will unlock the user resource which was previously locked either manually or automatically by the system. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `userRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(user);
     * let uri = params['model'].links('unlock').href;
     * ```
     * @method
     * @param data A user object used to unlock specified user resource.
     * @returns A promise that is resolved once the unlock user action has been performed.
     * @example //  user is a resource previously fetched using get action.
                        userClient.unlock(user)
                            .then(function (data) {
                                // perform success action here
                            },
                             function (response, status, headers, config) {
                                // perform error handling here
                            });
     **/
    UserClient.prototype.unlock = function (data) {
        return this.apiClient.put(this.userRoute.unlock(data), data);
    };
    /**
     * Returns a promise that is resolved once the lock user action has been performed. This action will lock the user resource out of the system. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `userRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(user);
     * let uri = params['model'].links('lock').href;
     * ```
     * @method
     * @param data A user object used to lock specified user resource.
     * @returns A promise that is resolved once the lock user action has been performed.
     * @example // user is a resource previously fetched using get action.
                    userClient.lock(user)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    UserClient.prototype.lock = function (data) {
        return this.apiClient.put(this.userRoute.lock(data), data);
    };
    /**
     * Returns a promise that is resolved once the approve user action has been performed. This action will mark the user resource as 'approved' in the system. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `userRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(user);
     * let uri = params['model'].links('approve').href;
     * ```
     * @method
     * @param data A user object used to approve specified user resource.
     * @returns A promise that is resolved once the approve user action has been performed.
     * @example // user is a resource previously fetched using get action.
                    userClient.approve(user)
                        .then(function (data) {
                            // perform success action here
                        })
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    UserClient.prototype.approve = function (data) {
        return this.apiClient.put(this.userRoute.approve(data), data);
    };
    /**
     * Returns a promise that is resolved once the disapprove user action has been performed. This action will mark the user resource as 'not approved' in the system. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `userRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(user);
     * let uri = params['model'].links('disapprove').href;
     * ```
     * @method
     * @example // user is a resource previously fetched using get action.
                    userClient.disapprove(user).then(function (data) {
                        // perform success action here
                    },
                     function (response, status, headers, config) {
                        // perform error handling here
                    });
     **/
    UserClient.prototype.disapprove = function (data) {
        return this.apiClient.put(this.userRoute.disapprove(data), data);
    };
    /**
     * Returns a promise that is resolved once the changePassword action has been performed. This action will update user's password selection.
     * @method
     * @param username A username or id which uniquely identifies user resource.
     * @param data A new password object used to update specified user password resource.
     * @returns A promise that is resolved once the changedPassword action has been performed.
     * @example userClient.changePassword('<username>', {
                    newPassword : '<new-password>',
                    sendMailNotification : false
                })
                .then(function () {
                    // perform success action here
                },
                 function (data, status, headers, config) {
                    // perform error handling here
                })
                .finally (function () {});
     **/
    UserClient.prototype.changePassword = function (username, data) {
        return this.apiClient.put(this.userRoute.changePassword(username), data);
    };
    var _a, _b;
    UserClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(_1.TYPES.UserRoute)),
        tslib_1.__param(1, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof _1.UserRoute !== "undefined" && _1.UserRoute) === "function" ? _a : Object, typeof (_b = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _b : Object])
    ], UserClient);
    return UserClient;
}());
exports.UserClient = UserClient;
/**
 * @overview
 ***Notes:**
 - Refer to the [Baasic REST API](http://dev.baasic.com/api/reference/home) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route definition.
 */ 
